import {
  Box,
  Button,
  Flex,
  Heading,
  Input as ChakraInput,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  Calendar,
  DollarSign,
  EllipsisVertical,
  Eye,
  FileText,
  Handshake,
  Hash,
  Info,
  Plus,
  Search,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import AgendamentoModal from '../../components/Modals/AgendamentoModal';
import CertificadoModal from '../../components/Modals/CertificadoModal';
import DefaultPage from '../../components/shared/DefaultPage';
import InfoCard from '../../components/shared/InfoCard';
import { Input } from '../../components/shared/Input';
import Table from '../../components/shared/Table';
import backend from '../../services/backend';
import { useCertificados } from '../../services/hooks/useCertificados';
import { useDadosCertificados } from '../../services/hooks/useDadosCertificados';
import { useDebounce } from '../../services/hooks/useDebounce';
import { formatCustomDate } from '../../utils/formatCustomDate';
import formatValue from '../../utils/formatValue';
import { phoneMask } from '../../utils/phoneMask';

const Certificados = () => {
  const color = useColorModeValue('#3f3f46', '#d4d4d8');
  const bg = useColorModeValue('white', 'gray.800');

  const inputBorder = useColorModeValue('gray.300', 'gray.600');
  const inputHoverBorder = useColorModeValue('gray.400', 'gray.500');

  const toast = useToast();
  const history = useHistory();

  const tiposLancamentos: Record<string, string> = {
    '1': 'Presencial',
    '2': 'Venda Rapida',
    '3': 'Visita Externa',
    '7': 'Video conferência',
    '9': 'Emissão automática',
  };

  const [certModal, setCertModal] = useState('');
  const [certModalOpen, setCertModalOpen] = useState(false);
  const [agendamentoOpen, setAgendamentoOpen] = useState(false);
  const [reagendamento, setReagendamento] = useState(false);
  const [search, setSearch] = useState('');
  const [gerandoRelatorio, setGerandoRelatorio] = useState(false);
  const [mesRelatorio, setMesRelatorio] = useState(
    new Date().toISOString().slice(0, 7),
  );
  const [clienteRelatorio, setClienteRelatorio] = useState('');

  const {
    isOpen: relatorioOpen,
    onOpen: abreRelatorio,
    onClose: fechaRelatorio,
  } = useDisclosure();

  const params: { idCertificado: string } = useParams();
  const location = useLocation();
  const tipo = location.pathname.includes('/detalhes/')
    ? 'detalhes'
    : location.pathname.includes('/agendamento/')
    ? 'agendamento'
    : '';

  const setaCertificado = (id: string) => {
    setCertModal(id);
    setCertModalOpen(true);
  };

  const setaCertAgendamento = (id: string, isReagendamento = false) => {
    setCertModal(id);
    setReagendamento(isReagendamento);
    setAgendamentoOpen(true);
  };

  const visualizaSenhaEmissao = async (id: string) => {
    if (!id || id === 'undefined') {
      toast({
        title: 'Nao foi possivel buscar a senha',
        description: 'Certificado sem UUID de venda.',
        position: 'bottom-right',
        status: 'error',
        isClosable: true,
      });
      return;
    }

    try {
      const response = await backend.get(`/certificado/${id}/senha-emissao`);

      toast({
        title: 'Senha de Emissao',
        description: response.data?.senha_emissao,
        position: 'bottom-right',
        status: 'info',
        duration: 10000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: 'Nao foi possivel buscar a senha',
        description:
          error?.response?.data?.message ?? 'Tente novamente em instantes.',
        position: 'bottom-right',
        status: 'error',
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    if (tipo === 'detalhes') {
      setaCertificado(params?.idCertificado);
    }
    if (tipo === 'agendamento') {
      setaCertAgendamento(params?.idCertificado);
    }
  }, []);

  const searchValue = useDebounce(search);
  const { data, isLoading, error } = useCertificados(searchValue);
  const { data: dados, isLoading: isDadosLoading } = useDadosCertificados();
  const certificadosData = data ?? [];

  const geraRelatorio = async () => {
    if (!mesRelatorio) {
      toast({
        title: 'Selecione um mes',
        description: 'Informe o mes antes de gerar o relatorio.',
        status: 'info',
        position: 'bottom-right',
        isClosable: true,
      });

      return;
    }

    const escapeHtml = (value: any) =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

    try {
      setGerandoRelatorio(true);

      const buscaPaginas = async (
        pagina: number,
        registros: any[] = [],
      ): Promise<any[]> => {
        const response = await backend.get('/certificados/relatorio', {
          params: {
            mes: mesRelatorio,
            cliente: clienteRelatorio.trim(),
            page: pagina,
            limit: 100,
          },
        });

        const novosRegistros = [...registros, ...(response.data?.rows ?? [])];

        if (response.data?.pagination?.has_more) {
          return buscaPaginas(pagina + 1, novosRegistros);
        }

        return novosRegistros;
      };

      const relatorio = await buscaPaginas(1);

      if (!relatorio.length) {
        toast({
          title: 'Nenhum certificado encontrado',
          description: 'Nao ha dados para os filtros selecionados.',
          status: 'info',
          position: 'bottom-right',
          isClosable: true,
        });

        return;
      }

      const colunasRelatorio = [
        'Venda',
        'Data Venda',
        'Master',
        'Parceiro',
        'Nome Certificado',
        'Situacao Soluti',
        'Data Aprovacao',
        'Produto',
      ];

      const linhas = relatorio
        .map((item: any) => {
          const valores = [
            item.venda,
            item.data_venda
              ? formatCustomDate(
                  item.data_venda,
                  'yyyy-MM-dd HH:mm:ss',
                  'dd/MM/yyyy',
                )
              : '',
            item.master,
            item.parceiro,
            item.nome_certificado,
            item.situacao_soluti,
            item.data_aprovacao,
            item.produto,
          ];

          return `
          <tr>
            ${valores.map(valor => `<td>${escapeHtml(valor)}</td>`).join('')}
          </tr>
        `;
        })
        .join('');

      const html = `
      <html>
        <head>
          <meta charset="UTF-8" />
        </head>
        <body>
          <h2>${escapeHtml(mesRelatorio)}</h2>
          <table border="1">
            <thead>
              <tr>
                ${colunasRelatorio
                  .map(coluna => `<th>${escapeHtml(coluna)}</th>`)
                  .join('')}
              </tr>
            </thead>
            <tbody>
              ${linhas}
            </tbody>
          </table>
        </body>
      </html>
    `;

      const blob = new Blob([html], {
        type: 'application/vnd.ms-excel;charset=utf-8;',
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = `relatorio-certificados-${mesRelatorio}.xls`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
      fechaRelatorio();
    } catch (error) {
      toast({
        title: 'Erro ao gerar relatorio',
        description: 'Nao foi possivel gerar o relatorio.',
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    } finally {
      setGerandoRelatorio(false);
    }
  };

  const colunas = React.useMemo(
    () => [
      {
        Header: 'PEDIDO',
        accessor: 'pedido',
      },
      {
        Header: 'CLIENTE',
        accessor: 'parceiros',
        Cell: ({ row }: { row: any }) => {
          const color = useColorModeValue('#3f3f46', 'white');
          return (
            <Flex flexDir="column">
              <Text color={color} fontSize="sm">
                {row.original.razao_nome}
              </Text>
              <Text color={color} fontSize="sm" mt="1">
                {phoneMask(row?.original?.celular)}
              </Text>
            </Flex>
          );
        },
      },
      {
        Header: 'vendedor',
        accessor: 'vendedor.nome',
      },
      {
        Header: 'produto',
        accessor: 'produto.descricao',
      },
      {
        Header: 'tipo',
        accessor: 'tipo_lancamento',
        Cell: ({ row }: { row: any }) => {
          const color = useColorModeValue('gray.700', 'white');
          return (
            <Text color={color}>
              {tiposLancamentos[row.original.tipo_lancamento]}
            </Text>
          );
        },
      },
      {
        Header: 'total',
        accessor: 'valor_venda',
        Cell: ({ row }: { row: any }) => {
          const color = useColorModeValue('gray.700', 'white');
          return (
            <Tooltip
              label={
                <>
                  Total: {formatValue(row.original.valor_venda)}
                  <br />
                  Sua Comissão: {formatValue(row.original.comissao_logado)}
                </>
              }
            >
              <Text color={color} fontSize="sm">
                {formatValue(row.original.valor_venda)}
              </Text>
            </Tooltip>
          );
        },
      },
      {
        Header: 'data',
        accessor: 'data_venda',
        Cell: ({ row }: { row: any }) => {
          const color = useColorModeValue('gray.700', 'white');
          return (
            <Text color={color} fontSize="sm">
              {formatCustomDate(
                row.original.data_venda,
                'yyyy-MM-dd HH:mm:ss',
                'dd/MM/yyyy',
              )}
            </Text>
          );
        },
      },
      {
        Header: 'status',
        accessor: 'status',
      },
      {
        Header: 'acoes',
        accessor: 'acoes',
        Cell: ({ row }: { row: any }) => {
          const color = useColorModeValue('gray.700', 'white');
          return (
            <Menu>
              <MenuButton p="1">
                <EllipsisVertical />
              </MenuButton>
              <MenuList>
                <MenuItem
                  p="10px"
                  onClick={() => setaCertificado(row.original.venda_uuid)}
                >
                  <Info />
                  <Text ml="2" color={color}>
                    Detalhes da Venda
                  </Text>
                </MenuItem>

                <MenuItem
                  p="10px"
                  onClick={() => visualizaSenhaEmissao(row.original.venda_uuid)}
                >
                  <Eye />
                  <Text ml="2" color={color}>
                    Ver Senha de Emissão
                  </Text>
                </MenuItem>

                <MenuItem
                  p="10px"
                  onClick={() => {
                    if (row.original.status === 'Cancelado') {
                      toast({
                        title: 'Não é possivel agendar',
                        description: 'Certificado cancelado',
                        position: 'bottom-right',
                        status: 'info',
                        isClosable: true,
                      });
                      return;
                    }
                    setaCertAgendamento(
                      row.original.venda_uuid,
                      row.original.data_videoconferencia !== null,
                    );
                  }}
                >
                  <Calendar />
                  <Text ml="2" color={color}>
                    {row.original.data_videoconferencia !== null
                      ? 'Reagendamento'
                      : 'Agendamento'}
                  </Text>
                </MenuItem>
              </MenuList>
            </Menu>
          );
        },
      },
    ],
    [],
  );

  return (
    <DefaultPage>
      <Flex mt="12" mx="4" justify="space-between" align="center">
        <Heading fontSize="2xl">Certificados Digitais</Heading>

        <Flex gap="5">
          <Button
            mr="4"
            variant="outline"
            p="6"
            leftIcon={<FileText size={22} />}
            onClick={abreRelatorio}
          >
            Gerar Relatório
          </Button>

          <Button
            bgGradient="linear(to-b, blue.400, blue.700)"
            p="6"
            leftIcon={<Plus size={24} />}
            onClick={() => history.push('/certificados/novo-certificado')}
            color="white"
          >
            <Text color="white">Nova Venda</Text>
          </Button>
        </Flex>
      </Flex>

      <SimpleGrid mt="4">
        <Input
          name="pesquisa"
          leftIcon={Search}
          bg={bg}
          color={color}
          placeholder="Buscar Certificado..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          h="50"
          w="100%"
          fontWeight="lg"
        />
      </SimpleGrid>

      <Box bg={bg} my="4" borderWidth="1px" rounded="2xl">
        <Table
          data={certificadosData}
          columns={colunas}
          isLoading={isLoading}
        />
      </Box>

      <Text fontWeight="medium" mb="2">
        Seus ganhos:
      </Text>

      <SimpleGrid
        templateColumns={['1fr', '1fr', '1fr 1fr 1fr']}
        spacingX="15px"
        spacingY="15px"
        mt="0"
        mb="4"
      >
        <InfoCard
          titulo="Minhas vendas"
          valor={formatValue(dados?.minhas_vendas ?? 0)}
          icon={DollarSign}
          cor="green.500"
          isLoading={isDadosLoading}
        />
        <InfoCard
          titulo="Vendas dos parceiros"
          valor={formatValue(dados?.vendas_parceiros ?? 0)}
          icon={Handshake}
          cor="blue.500"
          isLoading={isDadosLoading}
        />
        <InfoCard
          titulo="Numero total de vendas"
          valor={dados?.numero_vendas ?? 0}
          icon={Hash}
          cor="purple.500"
          isLoading={isDadosLoading}
        />
      </SimpleGrid>

      <Modal isOpen={relatorioOpen} onClose={fechaRelatorio} isCentered>
        <ModalOverlay />
        <ModalContent
          bg={useColorModeValue('white', 'gray.800')}
          color={useColorModeValue('gray.800', 'white')}
          borderWidth="1px"
          borderColor={useColorModeValue('gray.200', 'gray.700')}
          rounded="2xl"
        >
          <ModalHeader color={useColorModeValue('gray.800', 'white')}>
            Gerar Relatório
          </ModalHeader>
          <ModalCloseButton color={useColorModeValue('gray.800', 'white')} />

          <ModalBody>
            <Text mb="2" color={useColorModeValue('gray.600', 'gray.300')}>
              Selecione o mês do relatório
            </Text>

            <ChakraInput
              type="month"
              value={mesRelatorio}
              onChange={event => setMesRelatorio(event.target.value)}
              bg={useColorModeValue('white', 'gray.900')}
              color={useColorModeValue('gray.800', 'white')}
              borderColor={inputBorder}
              _hover={{
                borderColor: inputHoverBorder,
              }}
              _focus={{
                borderColor: 'blue.400',
                boxShadow: '0 0 0 1px #4299e1',
              }}
            />

            <Text
              mt="4"
              mb="2"
              color={useColorModeValue('gray.600', 'gray.300')}
            >
              Filtrar por cliente (opcional)
            </Text>

            <ChakraInput
              type="text"
              value={clienteRelatorio}
              onChange={event => setClienteRelatorio(event.target.value)}
              placeholder="Nome, fantasia ou CPF/CNPJ"
              autoComplete="off"
              bg={useColorModeValue('white', 'gray.900')}
              color={useColorModeValue('gray.800', 'white')}
              borderColor={inputBorder}
              _hover={{
                borderColor: inputHoverBorder,
              }}
              _focus={{
                borderColor: 'blue.400',
                boxShadow: '0 0 0 1px #4299e1',
              }}
            />
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr="3"
              onClick={fechaRelatorio}
              color={useColorModeValue('gray.700', 'gray.100')}
            >
              Cancelar
            </Button>

            <Button
              colorScheme="blue"
              onClick={geraRelatorio}
              isLoading={gerandoRelatorio}
            >
              Gerar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {certModalOpen && (
        <CertificadoModal
          isOpen={certModalOpen}
          close={() => {
            setCertModalOpen(false);
            history.push('/certificados');
          }}
          idCertificado={certModal}
        />
      )}

      {agendamentoOpen && (
        <AgendamentoModal
          isOpen={agendamentoOpen}
          close={() => {
            setAgendamentoOpen(false);
            setReagendamento(false);
            history.push('/certificados');
          }}
          idCertificado={certModal}
          reagendamento={reagendamento}
        />
      )}
    </DefaultPage>
  );
};

export default Certificados;
